<template>
    <setting-layout title="Withdraw">
        <v-container>
            <v-row justify="center">
                <div class="hint text-center">{{ $t('pages.profile.wallet.withdraw.available_balance') }}</div>
            </v-row>
            <v-row justify="center">
                <div class="hint text-center available">{{ currency }}{{ user.balance }}</div>
            </v-row>
            <v-row justify="center">
                <div class="big-text d-flex justify-center align-center text-center">
                    <span>{{ currency }}</span>
                    <v-text-field
                        class="total"
                        dense
                        solo
                        type="number"
                        v-model.number="total"
                        :rules="rules"
                        :min="0"
                        :max="1000000"
                        :error="errors.hasOwnProperty('amount')"
                        :error-messages="errors.hasOwnProperty('amount') ? errors.amount[0] : ''"
                    >
                    </v-text-field>
                </div>
            </v-row>

            <v-row>
                <v-col class=" bigger-font">
                    {{ $t('pages.profile.wallet.withdraw.total_gross') }}
                </v-col>
                <v-col class="text-end bigger-font">
                    {{ total_gross }}{{ currency }}
                </v-col>
            </v-row>

            <v-row>
                <v-col class="d-flex align-center bigger-font">
                    {{ $t('pages.profile.wallet.withdraw.additional_fee') }} 
                    <icon-info class="ml-1 mt-1" @click="showAdditionalFeeInfo = !showAdditionalFeeInfo" />
                </v-col>
                <v-col class="text-end bigger-font">
                    {{ additional_fee }}{{ currency }}
                </v-col>
            </v-row>
            <v-row v-if="showAdditionalFeeInfo">
                <div class="align-center ml-4">
                    {{ $t('pages.profile.wallet.withdraw.additinal_fee_info') }}
                </div>
            </v-row>

            <v-row>
                <v-col class="d-flex align-center bigger-font">
                    {{ $t('pages.profile.wallet.withdraw.noodzly_fee') }} 
                    <icon-info class="ml-1 mt-1" @click="showTagsInfo = !showTagsInfo" />
                </v-col>
                <v-col class="text-end bigger-font">
                    {{ noodzly_fee }}{{ currency }}
                </v-col>
            </v-row>
            <v-row v-if="showTagsInfo">
                <div class="align-center ml-4">
                    {{ $t('pages.profile.wallet.withdraw.total_net_info', 
                        { netfee: user.noodzler_fee != null && user.noodzler_fee !== '' ? user.noodzler_fee : user.default_net_fee }) }}
                </div>
            </v-row>

            <v-row>
                <v-col class="d-flex align-center bigger-font">
                    {{ $t('pages.profile.wallet.withdraw.total_net') }} 
                </v-col>
                <v-col class="text-end bigger-font">
                    {{ total_net }}{{ currency }}
                </v-col>
            </v-row>
            <v-row>
                <v-col>
                    <v-select
                        background-color="rgba(237,237,237, .1)"
                        color="orange"
                        :items="items"
                        label="Solo field"
                        solo
                        flat
                        item-color="orange"
                        item-text="text"
                        item-value="val"
                        v-model="paymentSystem"
                        :error="errors.hasOwnProperty('paymentSystem')"
                        :error-messages="errors.hasOwnProperty('paymentSystem') ? errors.paymentSystem[0] : ''"
                    ></v-select>
                </v-col>
            </v-row>
            <v-row class="pl-4 pr-4" justify="center">
                <gradient-button block @click="save"
                                 :disable="!canWithdrawn"
                                 :loading="$store.getters.loadingBtn(['setting', 'walletWithdrawBtn'])">
                                 {{ $t('pages.profile.wallet.withdraw.withdraw_btn') }}
                </gradient-button>
            </v-row>
        </v-container>
        <inform-dialog
            :text="infoDialog.msg"
            v-if="infoDialog.isShow"
            @close="infoDialog.isShow=false"
        />
        <verify-dialog
            v-if="isShowVerifyDialog"
            @close="isShowVerifyDialog=false"
        />
    </setting-layout>
</template>

<script>
import SettingLayout from "@/layouts/app/profile/SettingLayout";
import InformDialog from "@/components/app/dialogs/InformDialog";
import GradientButton from "@/components/app/button/GradientButton";
import {CURRENCY_SYMBOL, MIN_INTERNATIONAL_WIRE_TRANSFER_AMOUNT, MIN_CRYPTO_WALLET_AMOUNT, TRANSACTION_FEE} from "@/configs/constants";
import {mapActions, mapMutations, mapState} from "vuex";
import {DEFAULT_FEES} from "@/configs/constants";
import isNumber from "lodash/isNumber";
import Withdrawal from "@/models/Withdrawal";
import VerifyDialog from "@/components/app/dialogs/VerifyDialog.vue";
import IconInfo from "@/assets/images/icons/info.svg?inline";

export default {
    name: "Withdraw",
    data: () => ({
        paymentSystem: null,
        total: 0,
        currency: CURRENCY_SYMBOL,
        errors: {},
        isShowVerifyDialog: false,
        infoDialog: {
            isShow: false,
            msg: '',
        },
        showTagsInfo: false,
        showAdditionalFeeInfo: false,
    }),
    computed: {
        ...mapState({
            user: state => state.user.user.data,
        }),
        canWithdrawn() {
            return this.total > 0
        },
        rules() {
            return [
                v => isNumber(v) || `The amount must be a number.`,
                v => v > 0 || `Amount should be above ${CURRENCY_SYMBOL}0`,
                v => v <= 100000 || `Max should not be above ${CURRENCY_SYMBOL}100000`,
                v => v <= this.user.balance || `Your balance is insufficient`,
            ]
        },
        total_display: {
            get: function () {
                return this.total
            },
            set: function (newValue) {
                if (isNumber(newValue)) {
                    this.total = newValue
                }
            }
        },
        total_gross() {
            return Number(this.total).toFixed(2)
        },
        total_net() {
            return Number(this.total_gross - this.noodzly_fee - this.additional_fee).toFixed(2);
        },
        items() {
            return [
                {text: this.$t('pages.profile.wallet.withdraw.wire_transfer'), val: 'international_wire_transfer'},
                {text: this.$t('pages.profile.wallet.withdraw.bank_transfer'), val: 'bank'},
                {text: this.$t('pages.profile.wallet.withdraw.bitsafe'), val: 'bitsafe'},
                {text: this.$t('pages.profile.wallet.withdraw.crypto_wallet'), val: 'crypto_wallet'},
            ]
        },
        noodzly_fee() {
            // check net fee feom creator if creator fee is null fetch the default
            const creatorFee = this.user?.noodzler_fee;
            const defaultFee = this.user?.default_net_fee;
            const systemDefault = this.DEFAULT_FEES

            const appliedFee =
                creatorFee != null && creatorFee !== '' ? creatorFee
                : defaultFee != null && defaultFee !== '' ? defaultFee
                : systemDefault;

            const netFee = appliedFee / 100;
            const gross = Number(this.total_gross || 0);
            return (gross * netFee).toFixed(2);
        },
        additional_fee() {
            // Find the transaction fee object based on selected payment method
            const selectedFee = TRANSACTION_FEE.find(
                (fee) => fee.name === this.paymentSystem
            );

            if (selectedFee) {
                if (selectedFee.type === 'absolute' && this.total) {
                    return selectedFee.fee.toFixed(2);
                } else if (selectedFee.type === 'percent') {
                    const grossMinusNoodzly = this.total_gross - this.noodzly_fee;
                    return ((grossMinusNoodzly * selectedFee.fee) / 100).toFixed(2);
                }
            }
            return (0).toFixed(2);
        }
    },
    methods: {
        ...mapMutations([
            'showSnackBar',
        ]),
        ...mapActions([
            'getBitsafe',
            'getBankTransfer',
            'getCurrentUserData',
        ]),
        save() {
            this.errors = {};
            if (!(this.user.balance > 0)) {
                this.infoDialog.msg = 'Your balance is insufficient';
                this.infoDialog.isShow = true;
                return;
            }

            if (this.paymentSystem === 'bank' && this.total_gross < 100) {
                this.infoDialog.msg = this.$t('dialogs.withdrawal_warning.bank_transfer.description');
                this.infoDialog.isShow = true;
                return;
            }
            if (this.paymentSystem === 'bitsafe' && this.total_gross < 50) {
                this.infoDialog.msg = this.$t('dialogs.withdrawal_warning.bitsafe.description');
                this.infoDialog.isShow = true;
                return
            }
            if (this.paymentSystem === 'international_wire_transfer' && this.total_gross < MIN_INTERNATIONAL_WIRE_TRANSFER_AMOUNT) {
                this.infoDialog.msg = this.$t('dialogs.withdrawal_warning.wire_transfer.description', { min_amount: MIN_INTERNATIONAL_WIRE_TRANSFER_AMOUNT, currency: this.currency });
                this.infoDialog.isShow = true;
                return
            }
            if (this.paymentSystem === 'crypto_wallet' && this.total_gross < MIN_CRYPTO_WALLET_AMOUNT) {
                this.infoDialog.msg = this.$t('dialogs.withdrawal_warning.crypto_wallet.description', { min_amount: MIN_CRYPTO_WALLET_AMOUNT, currency: this.currency });
                this.infoDialog.isShow = true;
                return
            }

            new Withdrawal({
                paymentSystem: this.paymentSystem,
                amount: this.total_gross,
            }).save().then(() => {
                this.getCurrentUserData();
                this.errors = {}
                this.showSnackBar(this.$t('messages.success'))
                this.$router.push({name: 'user.own.profile.wallet'})
            }).catch(({response: res}) => {
                if (res.status === 400) {
                    if (res.data.err_key === 'unverified_account') {
                        this.isShowVerifyDialog = true
                        return
                    }
                }
                if (res.status === 422) {
                    this.errors = res.data.errors
                    return
                }
                this.showSnackBar(this.$t('messages.something_went_wrong'))
            })
        }
    },
    components: {
        VerifyDialog,
        InformDialog,
        GradientButton,
        SettingLayout,
        IconInfo
    },
    mounted() {
        if (this.items.length) {
            const { withdrawDefault } = this.$store.state.user.user.data;

            const isValidDefaultWithdraw = withdrawDefault && TRANSACTION_FEE.some(fee => fee.name === withdrawDefault);

            this.paymentSystem = isValidDefaultWithdraw ? withdrawDefault : this.items[0].val;
        }

        this.$mixpanel.identifyUser(this.user.email);
        this.$mixpanel.pageViewed('Withdraw');
    },
    created() {
        this.getBitsafe()
        this.getBankTransfer()
    }
}
</script>

<style scoped lang="scss">
@import "@/sass/_mixins";

.total {
    font-size: 3.6rem;
    max-width: 200px;
    position: relative;
}

.hint {
    color: rgba(255, 255, 255, 0.6);
}

.available {
    font-size: 1.2em;
}

.big-text {
    font-size: 3.6em;
}

.bigger-font {
    font-size: 1.2em;
    line-height: 1em;
}

.small-text {
    font-size: 0.7em;
    margin-top: -18px;
}
</style>

<style>
.total.v-text-field.v-text-field--enclosed .v-text-field__details {
    position: absolute;
    bottom: -16px;
    left: 0;
    width: 100%;
}

.total.v-text-field.v-text-field--solo.v-input--dense > .v-input__control {
    align-items: center;
    padding: 0;
    margin: 0;
}

.total.v-text-field.v-text-field--enclosed:not(.v-text-field--rounded) > .v-input__control > .v-input__slot {
    align-items: center;
    padding: 0;
    margin: 0;
}

.total.v-text-field.v-text-field--solo .v-input__control input {
    max-height: inherit !important;
}
</style>
